import React from 'react';
import { createRoot } from 'react-dom/client';
import '@invent/tailwind-integration/react';
import './store/root-domain'; // side-effect: initializes rootDomain before lib dependencies
// import { rootDomain } from './store/root-domain';
import { SharedHostGateway } from '@invent/platform-api';
import { baseHttpClient } from '@invent/http-client';
// import { createInspector, addEffect, addEvent, addStore } from 'effector-inspector';
import { v4 } from 'uuid';
import { updateFavicon } from '@/src/domains/branding/utils';
import { captureAuthorizationResponse } from '@/lib/oidc-client/authorization-response';
import { isSilentRenewFrame, postCodeToParent } from '@/lib/oidc-client/silent-renew-bridge';
import { isCodeFlow } from '@/lib/oidc-client/flow';
import {
  handOverRefreshTokenAcrossReloads,
  restoreRefreshTokenAfterReload
} from '@/lib/oidc-client/refresh-token';
import { identityKeyOf } from '@/lib/oidc-client/identity';
import { getTokenSyncParsed } from '@/lib/oidc-client/token-slot';
import { createWidgetLoadReporter } from '@/lib/widget-load-failures';
import { widgetActivity } from '@/src/shell/widget-activity';
import { leavePage, trackPageUnload } from '@/lib/page-lifecycle/page-lifecycle';
import { CURRENCY_STORAGE_KEY, withCurrencyParam } from '@/lib/currency-param/currency-param';
import {
  BootstrapMaintenanceError,
  BootstrapUnavailableError,
  loadBootstrap
} from '@/lib/bootstrap/load-bootstrap';
import { selectBootstrapURI } from '@/lib/bootstrap/bootstrap-uri';

// The code flow's silent-renew frame only forwards the authorization response to the
// window that owns the session — the refresh token lives in that window's memory, and
// in development this frame has no configuration at all. Keyed off `window.name`, which
// only the code flow's renewal sets: the legacy implicit frame uses `id="refresh"`, so
// keying off the id would short-circuit the implicit flow too and leave nothing to store
// its token.
const inSilentRenewFrame = isSilentRenewFrame();
if (inSilentRenewFrame) {
  postCodeToParent();
} else {
  // A refresh token the previous document in this tab handed over survives the reload,
  // so the first renewal after it is a refresh grant and not a `prompt=none` round-trip
  // through a frame that third-party cookie blocking breaks. Taken before anything can
  // renew; never in the frame, which shares the tab's sessionStorage but not this realm.
  // Only adopted if it belongs to whoever is in the shared token slot now.
  restoreRefreshTokenAfterReload(identityKeyOf(getTokenSyncParsed()));
  handOverRefreshTokenAcrossReloads();
}

// Read the authorization response off the URL and remove it now, synchronously, before
// anything can navigate. Doing it later — from inside React — would change the location
// while the token request is in flight and re-run the effects that decide whether we are
// authenticated; they conclude we are not, and redirect to /login mid-exchange.
const authorizationResponse = inSilentRenewFrame ? undefined : captureAuthorizationResponse();

const DEV_GATE_API_URI = 'https://gate.dev.apptrium.cloud/api/v1';
const UAT_GATE_API_URI = 'https://gate.uat.apptrium.cloud/api/v1';
const PROD_GATE_API_URI = 'https://gate.apptrium.cloud/api/v1';

// The gateway's `logger` slot is where platform-api reports a widget that failed to load
// (RemoteModule, its error boundary, useRemoteModuleContent). Empty since the Datadog
// browser logger was dropped; now one OTel log record per failure, through nginx to the
// SigNoz agent (PSDK-6533). The counter behind the failure-rate alert (TFLS-3279) is the
// effector model in src/domains/widget-loads, fed by module-federation's own load events
// and wired with the other domains in app.tsx. See README, "Widget load failure reports".
// Both stay quiet about loads a navigation cancelled, which they learn from `leavePage`
// and these listeners. Each failure record travels with the page's recent activity
// (PSDK-6558): what `widgetActivity` has been recording since its import, and the widget
// requests platform-api 1.25.0 and later report to the gateway's `activity` slot, bound
// here after construction until the shell depends on that version.
trackPageUnload();
window.sharedHostGateway = new SharedHostGateway({
  logger: createWidgetLoadReporter({ activity: widgetActivity })
});
window.sharedHostGateway.activity = widgetActivity.sink;

function selectCustomizationAPIURI() {
  if (origin.includes('.sandbox.apptrium.cloud') || window.APP_ENV === 'DEV') {
    return DEV_GATE_API_URI;
  }
  if (origin.includes('.rc.apptrium.cloud') || window.APP_ENV === 'UAT') {
    return UAT_GATE_API_URI;
  }
  return PROD_GATE_API_URI;
}

// It is important to hold this code - or any rootDomain-dependent invocations -
// in main.tsx (right at the app entrypoint)
// so rootDomain will be initialized and shared into the scope before lib dependencies
// arrive in runtime, trying to locate if rootDomain is shared
// if (process.env.NODE_ENV !== 'production') {
//   rootDomain.onCreateStore(addStore);
//   rootDomain.onCreateEffect(addEffect);
//   rootDomain.onCreateEvent(addEvent);
//
//   createInspector({ trimDomain: 'app' });
// }

function maintenanceCondition(envConfigJson: Record<string, any>) {
  return Boolean(envConfigJson.MAINTENANCE_MODE);
}

// async imports are here to delay appearance of modules that could have unresolved dependencies
async function main() {
  const { catchAndNotify, showNotification } = await import('./handlers');
  window.sharedHostGateway.registerNotifications({ catchAndNotify, showNotification });

  // `idp` and `login_hint` only ever appear on the URL the portal was launched with,
  // and the router replaces that URL with /login before the login widget mounts.
  // Stash them now so the redirect to /connect/authorize can still honour them.
  const { captureLaunchParameters } = await import('@/lib/oidc-client');
  captureLaunchParameters();

  const bootstrapUrl = selectBootstrapURI({
    origin: window.location.origin,
    appEnv: window.APP_ENV,
    switchboardApi: window.SWITCHBOARD_API,
    isDevelopment: process.env.NODE_ENV === 'development'
  });
  let isInMaintenance = false;
  // The configuration could not be fetched at all — the network, not maintenance.
  let isUnreachable = false;
  try {
    const envConfigJson = await loadBootstrap(bootstrapUrl);
    // TODO: https://wcloud.atlassian.net/browse/PSDK-5350
    // currently we do request about favicon from saas-bff
    // and we mutate CUSTOMIZATION_BE in the scope
    // this is not ok

    if ((envConfigJson?.CUSTOM_ENV?.flags?.useGateApi ?? false) && !origin.includes('localhost')) {
      // remove after full migration to Gate API
      // using for switch on https://saas-bff.apptrium.cloud -> https://gate.apptrium.cloud/api/v1
      envConfigJson.CUSTOMIZATION_BE = selectCustomizationAPIURI();
    }
    updateFavicon(envConfigJson.CUSTOMIZATION_BE, envConfigJson.API_BASE_URL);
    sessionStorage.setItem('tenentId', envConfigJson.TENENT_KEY);

    Object.assign(window, envConfigJson);
    isInMaintenance = maintenanceCondition(envConfigJson);
    baseHttpClient.interceptors.request.add((...args) => {
      const [_apiUrl, options] = args;
      const apiUrl = _apiUrl
        .replace('_internal_api_', window.API_BASE_URL)
        .replace('_fe_be_api_', window.CUSTOMIZATION_BE);
      return [apiUrl, options];
    });
    baseHttpClient.interceptors.request.add((apiUrl, options) =>
      withCurrencyParam(
        apiUrl,
        options,
        window.API_BASE_URL,
        localStorage.getItem(CURRENCY_STORAGE_KEY)
      )
    );
  } catch (error) {
    if (error instanceof BootstrapUnavailableError) {
      isUnreachable = true;
      console.error('[Bootstrap]', error.message, error.cause);
    } else if (error instanceof BootstrapMaintenanceError) {
      isInMaintenance = true;
    } else {
      isInMaintenance = true;
      catchAndNotify({
        message: 'Failed to get project configuration. Contact invent admin',
        traceId: 'bootstrap-1'
      });
    }
  }
  const boots = !isInMaintenance && !isUnreachable;

  // Has to run after the bootstrap response is merged onto `window` — until then there
  // is no OIDC_BASE_URL or OIDC_CLIENT_ID to build the token request from — and before
  // the first render, so EntryPoint finds a stored token and takes its ordinary path.
  // The flow is only knowable here, after the bootstrap merge — which is exactly why the
  // capture above cannot be gated on it. An implicit portal never receives a code (its
  // response arrives in the fragment), so this block is dead code for `po-` clients.
  if (authorizationResponse && boots && isCodeFlow()) {
    try {
      const { completeAuthorizationCodeFlow } = await import('@/lib/oidc-client/callback');
      const { clearSessionArtifacts, markFreshLogin } =
        await import('@/src/domains/auth/session/session-cap');
      const { STORAGE_KEYS } = await import('@/lib/oidc-client');
      const { peekTransaction } = await import('@/lib/oidc-client/tx-store');

      // A silent page-level authorization (boot-handle.ts) continues the same session; it
      // only fetched this tab a refresh token. It must not reset what a login resets.
      const continuesSession = Boolean(
        authorizationResponse.state && peekTransaction(authorizationResponse.state)?.silent
      );

      // A fresh login starts a fresh session window. Without this the absolute-session
      // cap keeps the previous session's start time and expires within seconds. It is
      // also the one interactive login this tab will report to analytics.
      if (!continuesSession) {
        clearSessionArtifacts();
        markFreshLogin();
      }

      const { deepLink } = await completeAuthorizationCodeFlow(authorizationResponse);
      if (!continuesSession || !sessionStorage.getItem(STORAGE_KEYS.SESSION_ID)) {
        sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, v4());
      }

      if (deepLink) {
        if (/^https?:\/\//i.test(deepLink)) {
          leavePage(deepLink);
          return;
        }
        window.history.replaceState(null, '', deepLink.startsWith('/') ? deepLink : `/${deepLink}`);
      }
    } catch (error) {
      // The page-level `prompt=none` that a tab opening without a refresh token tries
      // (boot-handle.ts) found no session at the IdP: sign in now, back to the same place.
      const { signInAfterSilentTopLevelFailed } = await import('@/src/domains/auth/boot-handle');
      if (await signInAfterSilentTopLevelFailed(authorizationResponse)) return;

      // Leave the tokens absent and carry on rendering: EntryPoint finds no token and
      // routes to /login, which is the right outcome for a login that did not complete.
      console.error('[Auth] Authorization code exchange failed:', error);
    }
  }

  // Requests from every widget wait for a token about to expire to be renewed first,
  // rather than go out with it (`beforeToken`, @invent/http-client 2.1). Code flow only:
  // the implicit flow has no backoff, and a hook there would restart a failing renewal
  // on every request.
  if (boots && isCodeFlow()) {
    const { installTokenFreshnessGuard } = await import('@/src/domains/auth/token-freshness-guard');
    installTokenFreshnessGuard();
  }

  // Renewals across tabs: a tab without a refresh token asks one that has it before
  // falling back to the `prompt=none` frame. After the exchange above, so a tab that just
  // logged in announces itself as a holder; before the app, so the first heartbeat
  // already coordinates. Code flow only: the implicit flow has no refresh tokens.
  //
  // Not on /close either: integration OAuth popups (proxy-app-components) land there,
  // post to their opener and close at once. Coordinating, the popup would announce itself
  // as a live tab for a moment — and a holder leaving just then could hand its refresh
  // token to a window that is about to vanish.
  if (boots && isCodeFlow() && window.location.pathname !== '/close') {
    const { installRefreshCoordination } =
      await import('@/src/domains/auth/refresh-coordination-install');
    installRefreshCoordination();
  }

  const root = createRoot(document.getElementById('root')!);
  if (boots) {
    const App = await import('./app');
    root.render(<App.default />);
  } else {
    // Only the service page's chunk: offline, every extra request is one more to fail.
    const ServicePages = await import('./service-pages/');
    root.render(
      isUnreachable ? (
        <ServicePages.NoConnection bootstrapUrl={bootstrapUrl} />
      ) : (
        <ServicePages.Maintenance />
      )
    );
  }

  // A tab that opened without a refresh token gets one now, in the background — through
  // the whole page if the hidden frame cannot sign in (third-party cookies blocked).
  if (boots && isCodeFlow()) {
    import('@/src/domains/auth/boot-handle')
      .then(({ secureRefreshHandleAtBoot }) => secureRefreshHandleAtBoot())
      .then((outcome) => console.info('[Token Worker]', 'refresh token at startup:', outcome))
      .catch((error) =>
        console.warn('[Token Worker]', 'refresh token at startup failed:', error?.message)
      );
  }
}

// Never in the silent-renew frame: it has already forwarded its response upward and
// booting the app there would duplicate the heartbeat, the idle ticker and every widget.
if (!inSilentRenewFrame) {
  main();
}
